'use client '

import { siteConfig } from '@/config/site.config'
import { cn } from '@/lib/utils/utils'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { getLocalizedUri, LangCode } from '../i18n/lang-context'

export const Logo = ({ imageSrc, imageDarkSrc, uri = '', onClick, langCode, className = '' }: { imageSrc: string, imageDarkSrc: string, uri?: string, onClick?: () => void, langCode: LangCode, className?: string }) => {

  return (
    <Link href={uri !== undefined ? getLocalizedUri(uri, langCode) : ''} onClick={onClick} className={cn('relative', className)}>
      <Image alt={siteConfig.appName}
        src={imageSrc}
        width={0}
        height={0}
        sizes='33vw'
        priority={true}
        className='block h-full w-auto object-cover transition-all hover:scale-110 dark:hidden'
      />
      <Image alt={siteConfig.appName}
        src={imageDarkSrc}
        width={0}
        height={0}
        sizes='33vw'
        priority={true}
        className='hidden h-full w-auto object-cover transition-all hover:scale-110 dark:block'
      />
    </Link>
  )
}

export const LogoHorizontal = ({ bj = false, uri = '', onClick, langCode, className = '' }: { bj?: boolean, uri?: string, onClick?: () => void, langCode: LangCode, className?: string }) => {

  const imageSrc = bj && siteConfig.logo.horizontalBj ? siteConfig.logo.horizontalBj : siteConfig.logo.horizontal
  const imageDarkSrc = bj && siteConfig.logo.horizontalBjWhite ? siteConfig.logo.horizontalBjWhite : siteConfig.logo.horizontalWhite

  const sizeClass = 'h-[40px] md:h-[49px] w-auto'
  const sizeClassBJ = 'h-[60px] md:h-[74] w-auto'

  const sc =
    'light:' + (bj && siteConfig.logo.horizontalBj ? sizeClassBJ : sizeClass) +
    ' dark:' + (bj && siteConfig.logo.horizontalBjWhite ? sizeClassBJ : sizeClass)

  return (
    <Logo imageSrc={imageSrc} imageDarkSrc={imageDarkSrc} uri={uri} onClick={onClick} langCode={langCode} className={cn(sc, className)} />
  )
}

export const LogoVertical = ({ uri = '', onClick, langCode, className = '' }: { uri?: string, onClick?: () => void, langCode: LangCode, className?: string }) => {

  const imageSrc = siteConfig.logo.vertical
  const imageDarkSrc = siteConfig.logo.verticalWhite

  const sizeClass = 'h-[80px] w-auto'

  return (
    <Logo imageSrc={imageSrc} imageDarkSrc={imageDarkSrc} uri={uri} onClick={onClick} langCode={langCode} className={cn(sizeClass, className)} />
  )
}

export function LogoIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 167.01 160.55"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g id="picto_n">
        <g>
          <path fill="currentColor" d="M121.12,4.35c-1.55-1.44-3.12-2.85-4.72-4.24h8.41c7.32.03,13.81,4.75,16.06,11.72l4.67,14.44c-.09-.05-.18-.11-.27-.17-3.05-1.92-5.91-4.09-8.65-6.43-5.52-4.66-10.26-10.35-15.5-15.32Z" />
          <path fill="currentColor" d="M79.77.05l13.5.02c3.91,2.71,7.93,5.26,12,7.73,6.08,3.95,12.46,7.38,19.04,10.47,6.54,3.15,12.79,6.77,18.34,11.43,2.04,1.69,3.94,3.56,5.78,5.51h0s6.78,20.97,6.78,20.97c-1.11-.73-2.23-1.43-3.39-2.09-6.26-3.6-13.2-6.29-19.21-10.23-8.39-5.28-15.38-12.57-22.45-19.52-3.25-3.13-6.73-6.01-10.2-8.87-6.41-5.56-13.15-10.7-20.18-15.41Z" />
          <path fill="currentColor" d="M69.86,20.52C59.25,13.28,48.31,6.49,36.66,1.03,38.49.36,40.46,0,42.49,0l18.25.03c10.02,9.23,20.92,17.43,32.59,24.53,4.52,2.92,9.17,5.81,13.98,8.27,8.04,4.11,16.13,8.36,23.05,14.12,5.59,4.52,10.27,10.3,15.62,15.16,4.47,4.05,9.46,7.4,14.57,10.57l5.65,17.48c.56,1.74.83,3.51.82,5.27-3.44-3.63-7.12-6.99-10.9-10.26-5.5-4.64-11.07-9.21-17.26-12.91-6.19-3.72-12.93-6.65-18.92-10.64-3.01-1.97-5.87-4.14-8.67-6.41-5.6-4.54-10.64-9.8-16.05-14.58-8.22-6.97-16.57-13.86-25.35-20.1Z" />
          <path fill="currentColor" d="M2.15,85.9l94.62,68.95-3.42,2.48c-5.93,4.3-13.95,4.29-19.88-.03L6.95,108.82c-5.92-4.31-8.39-11.95-6.11-18.91l1.31-4.01Z" />
          <path fill="currentColor" d="M114.91,141.72l-15.72,11.38c-1.41-1.06-2.92-2.16-4.49-3.31-6.89-5-14.32-10.11-20.75-14.5-2.27-1.57-4.58-3.21-7.07-5.02-6.21-4.51-12.41-9.27-18.41-13.87-1.58-1.21-3.16-2.43-4.75-3.64-8.73-6.6-16.68-12.5-24.31-18.04-5.62-4.08-11.04-7.96-16.34-11.67l6.06-18.57c17.38,14,35.66,26.9,54.33,39.11,12.2,7.68,23.38,16.72,34.49,25.86,5.54,4.25,11.19,8.34,16.95,12.27Z" />
          <path fill="currentColor" d="M132.92,128.68l-15.31,11.08c-.11-.08-.21-.17-.32-.25-5.68-4.4-11.4-8.76-17.35-12.78-12.1-7.78-24.13-15.66-35.18-24.93-17.58-14.24-35.72-27.86-54.66-40.27l5.99-18.33c7.67,6.72,15.67,13.04,23.78,19.19,8.67,6.41,17.6,12.46,26.6,18.4,9.09,5.8,18.48,11.2,26.97,17.88,8.5,6.63,16.15,14.37,25.13,20.42,4.71,3.3,9.47,6.54,14.34,9.59Z" />
          <path fill="currentColor" d="M151.4,115.3l-15.22,11.02c-1.96-1.66-3.94-3.29-5.94-4.92-5.62-4.49-11.29-8.91-17.32-12.83-6.03-3.92-12.41-7.37-18.32-11.45-5.94-4.06-11.55-8.56-17.02-13.24-18.96-16.16-38.96-31.23-60.4-43.96l6.06-18.57c16.61,16.42,35.54,30.14,55.26,42.6,9.23,5.59,18.85,10.72,27.26,17.49,5.67,4.42,10.71,9.7,16.22,14.35,5.51,4.64,11.5,8.62,17.55,12.52,3.89,2.46,7.83,4.84,11.88,7Z" />
          <path fill="currentColor" d="M160.02,109.05l-5.75,4.17c-3.55-3.48-7.28-6.74-11.1-9.94-5.56-4.57-11.18-9.05-17.29-12.87-6.11-3.82-12.67-7.01-18.62-11.05-5.97-4.02-11.54-8.57-16.89-13.42-16.05-14.44-33.2-27.68-51.69-38.87-4.77-2.89-9.61-5.71-14.62-8.28l2.33-7.13c1.31-3.99,4-7.25,7.46-9.29,1.71,1.88,3.47,3.68,5.25,5.46,12.83,12.69,27,23.89,42.29,33.47,4.51,2.98,9.07,5.89,13.83,8.48,7.92,4.32,15.83,8.71,22.83,14.42,5.63,4.47,10.49,10,15.92,14.75,5.42,4.76,11.48,8.66,17.58,12.48,4.85,2.95,9.76,5.74,14.94,8.09-.95,3.75-3.18,7.17-6.47,9.55Z" />
        </g>
      </g>
    </svg>
  )
}